import type { ConfigIssueCode } from '@acs/shared';
import { Banner } from '@acs/ui';
import { useI18n, type TranslationKey } from '../../i18n/index.js';
import { useReadiness } from './hooks.js';

/**
 * What the assistant is running on, in the owner's words (doc 33 W9 #9–10).
 *
 * Errors mean the last save was refused for calls: the PREVIOUS configuration
 * is still answering. Warnings are things worth fixing. Operator detail (field
 * paths) is not shown here; support reads it from the readiness document.
 */
export function ReadinessBanner({ businessId }: { businessId: string }) {
  const { t } = useI18n();
  const { data } = useReadiness(businessId);
  if (!data) return null;
  const errors = data.issues.filter((i) => i.severity === 'error');
  const warnings = data.issues.filter((i) => i.severity === 'warning' && i.code !== 'implicit_hours');
  if (data.ok && warnings.length === 0) return null;
  const line = (code: ConfigIssueCode) => t(`readiness.${code}` as TranslationKey);
  return (
    <div style={{ marginBottom: 16 }}>
      <Banner tone={data.ok ? 'warning' : 'danger'} title={t(data.ok ? 'readiness.warningsTitle' : 'readiness.notPublishedTitle')}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {[...new Set([...errors, ...warnings].map((i) => i.code))].map((code) => (
            <li key={code}>{line(code)}</li>
          ))}
        </ul>
      </Banner>
    </div>
  );
}
